
import { computed, defineComponent, PropType, ref } from 'vue'
import useQuasar from 'quasar/src/composables/use-quasar.js';
import { AgreementMember } from '@/modules/agreements/types'
import AgreementSwapMember from '@/components/agreements/dialogs/updating-member/swap-member.vue'
import AgreementCancelMember from '@/components/agreements/dialogs/updating-member/cancel-member.vue'
import AgreementPurgeMember from '@/components/agreements/dialogs/updating-member/purge-member.vue'
import AgreementResetMemberEndDate from '@/components/agreements/dialogs/updating-member/reset-member-end-date.vue'
import { useRoute, useRouter } from 'vue-router'
import { MembersRouteNames } from '@/router/routes-names'
import ResourceLink from '@/components/_shared/links/resource-link.vue'

export default defineComponent({
    name: 'agreement-single-member',
    components: { ResourceLink, AgreementSwapMember },
    props: {
        member: {
            type: Object as PropType<AgreementMember>,
            required: true
        },
        agreementId: {
            type: String,
            required: true
        },
        draft: {
            type: Boolean,
            required: true
        },
        expired: {
            type: Boolean,
            required: true
        }
    },
    setup (props) {
        const $q = useQuasar()
        const router = useRouter()
        const route = useRoute()
        const accessTypeDetails = {
            active: {
                status: {
                    label: 'Access on-going',
                    color: 'positive'
                },
                buttons: ['purge', 'cancel', 'swap']
            },
            expired: {
                status: {
                    label: 'Expired',
                    color: 'negative'
                },
                buttons: [] as string[]
            },
            draft: {
                status: {
                    label: 'Access in draft',
                    color: 'blue-10'
                },
                buttons: ['cancel']
            },
            'cancelled/active': {
                status: {
                    label: 'Access cancelled',
                    color: 'orange-7'
                },
                buttons: ['purge', 'reset-end-date']
            },
            'cancelled/expired': {
                status: {
                    label: 'Access cancelled',
                    color: 'orange-7'
                },
                buttons: [] as string[]
            }
        }

        const memberAccessStatus = computed(() => {
            return accessTypeDetails[memberAccessType.value].status
        })

        const memberAccessType = computed(() => {
            const m = props.member
            if (props.draft) return 'draft'
            else if (m.isActive && !m.cancelled) return 'active'
            else if (m.isActive && m.cancelled) return 'cancelled/active'
            else if (m.expired && m.cancelled) return 'cancelled/expired'
            else return 'expired'
        })

        function showButton (buttonName: string) {
            if (!buttonName) return false
            return accessTypeDetails[memberAccessType.value].buttons.includes(buttonName)
        }

        const cancelButtonLabel = computed(() => props.draft ? 'Remove' : 'Cancel')
        const showSwapMemberDialog = ref(false)
        function onSwapMember () {
            showSwapMemberDialog.value = true
        }

        function onHideSwapMember () {
            showSwapMemberDialog.value = false
        }

        function onCancelMember () {
            $q.dialog({
                component: AgreementCancelMember,
                componentProps: {
                    agreementId: props.agreementId,
                    member: props.member,
                    draft: props.draft
                }
            })
        }

        function onPurgeMember () {
            $q.dialog({
                component: AgreementPurgeMember,
                componentProps: {
                    agreementId: props.agreementId,
                    member: props.member
                }
            })
        }

        function onResetMemberEndDate () {
            $q.dialog({
                component: AgreementResetMemberEndDate,
                componentProps: {
                    agreementId: props.agreementId,
                    member: props.member
                }
            })
        }

        const memberAsResource = computed(function () {
            return {
                type: 'member',
                id: props.member.id,
                alias: props.member.id,
                text: props.member.email
            }
        })

        return {
            memberAccessStatus,
            onSwapMember,
            onHideSwapMember,
            onCancelMember,
            onPurgeMember,
            onResetMemberEndDate,
            cancelButtonLabel,
            showSwapMemberDialog,
            showButton,
            memberAsResource
        }
    }
})
